import { useState, useEffect } from 'react';
import { MapPin, Briefcase, Clock, ChevronDown, ChevronUp, Send, Upload, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { jobService, jobApplicationService, emailService, operationLogService } from '@/services/storage';
import type { Job, JobApplication } from '@/types';
import { toast } from 'sonner';

export default function Jobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    const activeJobs = await jobService.getActive();
    setJobs(activeJobs);
  };

  const toggleJob = (id: string) => {
    setExpandedJob(expandedJob === id ? null : id);
  };

  const handleApply = (job: Job) => {
    setSelectedJob(job);
    setFormData({ name: '', email: '', phone: '', coverLetter: '' });
    setResumeFile(null);
    setApplyDialogOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        toast.error('请上传PDF或Word格式的文件');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('文件大小不能超过10MB');
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('请填写必填项');
      return;
    }

    if (!resumeFile) {
      toast.error('请上传简历文件');
      return;
    }

    if (!selectedJob) return;

    // 读取简历文件为base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const application: JobApplication = {
        id: Date.now().toString(),
        jobId: selectedJob.id,
        jobTitle: selectedJob.title,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        resumeUrl: reader.result as string,
        resumeName: resumeFile.name,
        coverLetter: formData.coverLetter,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      jobApplicationService.add(application);
      
      // 发送邮件通知
      await emailService.sendJobApplicationNotification(application);

      operationLogService.add({
        user: formData.name,
        action: '投递简历',
        module: '简历投递',
        details: `投递职位: ${selectedJob.title}`,
      });

      toast.success('简历投递成功，我们会尽快与您联系！');
      setApplyDialogOpen(false);
    };
    reader.readAsDataURL(resumeFile);
  };

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              加入我们
            </h1>
            <p className="text-xl text-blue-100">
              与瀚昕微一起成长，共创美好未来
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">为什么选择瀚昕微</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              我们提供有竞争力的薪酬福利和良好的职业发展空间
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: '有竞争力的薪酬', desc: '具有市场竞争力的薪资水平' },
              { title: '股权激励', desc: '核心员工享有股权激励计划' },
              { title: '职业发展', desc: '完善的培训和晋升体系' },
              { title: '团队氛围', desc: '开放、创新的工作环境' },
            ].map((item, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">热招职位</h2>

          {jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Card key={job.id} className="overflow-hidden">
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleJob(job.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-1" />
                            {job.department}
                          </span>
                          <span className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {job.location}
                          </span>
                          <span className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {job.type}
                          </span>
                          <Badge variant="secondary">{job.experience}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApply(job);
                          }}
                        >
                          立即申请
                        </Button>
                        {expandedJob === job.id ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedJob === job.id && (
                    <div className="px-6 pb-6 border-t bg-gray-50">
                      <div className="pt-4">
                        <h4 className="font-bold mb-2">职位描述</h4>
                        <p className="text-gray-600 mb-4">{job.description}</p>

                        <h4 className="font-bold mb-2">任职要求</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-600">
                          {job.requirements.map((req, index) => (
                            <li key={index}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              暂无招聘职位
            </div>
          )}
        </div>
      </section>

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>申请职位 - {selectedJob?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="applyName">姓名 *</Label>
              <Input
                id="applyName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入您的姓名"
                required
              />
            </div>
            <div>
              <Label htmlFor="applyEmail">邮箱 *</Label>
              <Input
                id="applyEmail"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="请输入您的邮箱"
                required
              />
            </div>
            <div>
              <Label htmlFor="applyPhone">电话 *</Label>
              <Input
                id="applyPhone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="请输入您的电话"
                required
              />
            </div>
            <div>
              <Label htmlFor="coverLetter">自我介绍</Label>
              <Textarea
                id="coverLetter"
                value={formData.coverLetter}
                onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                placeholder="请简要介绍自己..."
                rows={4}
              />
            </div>
            <div>
              <Label>简历上传 *</Label>
              <div className="mt-2">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    id="resume-upload"
                  />
                  <label htmlFor="resume-upload" className="cursor-pointer">
                    {resumeFile ? (
                      <div className="flex items-center justify-center">
                        <FileText className="h-8 w-8 text-blue-500 mr-2" />
                        <span className="text-gray-700">{resumeFile.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">点击上传简历</p>
                      </>
                    )}
                  </label>
                </div>
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  <span>支持 PDF、Word 格式，文件大小不超过 10MB</span>
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full">
              <Send className="mr-2 h-4 w-4" />
              提交申请
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
